import React from "react";
import { AiLoadError, useAiRequestGuard, withAiAgentScope } from "./aiAgentRequests";
import useAiAgentDialog from "./useAiAgentDialog";
import AiAnswerContentNav from "./AiAnswerContentNav";
import { normalizeSenderList, senderLabel } from "./aiAgentSenders";
import {
  FileText,
  Image as ImageIcon,
  Pencil,
  RefreshCcw,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  deleteAiMediaAsset,
  getAiMediaAsset,
  getAiMediaAssetSenders,
  getAiMediaAssets,
  updateAiMediaAsset,
  uploadAiMediaAsset,
} from "./api/aiAgentApi";

const CATEGORIES = [
  "BeforeAfter",
  "Testimonial",
  "Brochure",
  "Catalog",
  "PriceList",
  "Menu",
  "ProductImage",
  "ServiceImage",
  "PropertyPhoto",
  "FloorPlan",
  "LocationMap",
  "CaseStudy",
  "Guide",
  "ReportSample",
  "Offer",
  "TeamIntro",
  "Other",
];

const CONSENT_REQUIRED_CATEGORIES = new Set([
  "BeforeAfter",
  "Testimonial",
  "CaseStudy",
]);

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "video/mp4",
  "video/3gpp",
];

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_PDF_BYTES = 16 * 1024 * 1024;
const MAX_VIDEO_BYTES = 16 * 1024 * 1024;
const ACCEPTED_FILE_INPUT_TYPES = ACCEPTED_FILE_TYPES.join(",");
const DELIVERY_MESSAGE_PLACEMENTS = [
  { value: "CaptionOnly", label: "Use media caption only" },
  { value: "BeforeMedia", label: "Send text before media" },
  { value: "AfterMedia", label: "Send text after media" },
  {
    value: "BeforeAndAfterMedia",
    label: "BeforeAndSend text before and after media",
  },
  { value: "None", label: "Send media only" },
];

const EMPTY_FORM = {
  file: null,
  title: "",
  description: "",
  category: "Brochure",
  whatsAppMediaId: "",
  deliveryMessagePlacement: "CaptionOnly",
  beforeMediaMessageTemplate: "",
  afterMediaMessageTemplate: "",
  approvedCaption: "",
  requiredDisclaimer: "",
  approvedForAutoSend: false,
  requiresHumanApproval: true,
  consentConfirmed: false,
  isActive: true,
  expiresAtUtc: "",
  language: "",
  tags: "",
  useCases: "",
  priority: 0,
  country: "",
  state: "",
  city: "",
  area: "",
  pincode: "",
};

function boolOptionValue(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  return "";
}

function fromBoolOption(value) {
  if (value === "true") return true;
  if (value === "false") return false;
  return "";
}

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString();
}

function formatDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function dateInputToUtc(value) {
  return value ? `${value}T00:00:00.000Z` : null;
}

function listToJsonArray(value) {
  const items = String(value || "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
  return items.length ? JSON.stringify(items) : null;
}

function jsonArrayToList(value) {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.join(", ") : "";
  } catch {
    return "";
  }
}

function extensionFor(fileName) {
  const dot = String(fileName || "").lastIndexOf(".");
  return dot >= 0 ? String(fileName).slice(dot).toLowerCase() : "";
}

function validateFile(file) {
  if (!file) return "File is required.";
  if (!file.size) return "File is empty.";

  const type = String(file.type || "").toLowerCase();
  const ext = extensionFor(file.name);
  const validImage =
    (type === "image/jpeg" && [".jpg", ".jpeg"].includes(ext)) ||
    (type === "image/png" && ext === ".png") ||
    (type === "image/webp" && ext === ".webp");
  const validPdf = type === "application/pdf" && ext === ".pdf";
  const validVideo =
    (type === "video/mp4" && ext === ".mp4") ||
    (type === "video/3gpp" && [".3gp", ".3gpp"].includes(ext));

  if (!ACCEPTED_FILE_TYPES.includes(type) || (!validImage && !validPdf && !validVideo)) {
    return "Use a JPG, PNG, WebP, PDF, MP4, or 3GP file.";
  }

  if (validImage && file.size > MAX_IMAGE_BYTES) {
    return "Images must be 10 MB or smaller.";
  }

  if (validPdf && file.size > MAX_PDF_BYTES) {
    return "PDF documents must be 16 MB or smaller.";
  }

  if (validVideo && file.size > MAX_VIDEO_BYTES) {
    return "Videos must be 16 MB or smaller.";
  }

  return null;
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.message ||
    fallback
  );
}

function badgeClasses(active, tone = "green") {
  if (!active) return "border-slate-200 bg-slate-50 text-slate-600";
  if (tone === "amber") return "border-amber-200 bg-amber-50 text-amber-700";
  if (tone === "rose") return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function toEditForm(detail) {
  return {
    ...EMPTY_FORM,
    title: detail?.title || "",
    description: detail?.description || "",
    category: detail?.category || "Brochure",
    whatsAppMediaId: detail?.whatsAppMediaId || "",
    deliveryMessagePlacement: detail?.deliveryMessagePlacement || "CaptionOnly",
    beforeMediaMessageTemplate: detail?.beforeMediaMessageTemplate || "",
    afterMediaMessageTemplate: detail?.afterMediaMessageTemplate || "",
    approvedCaption: detail?.approvedCaption || "",
    requiredDisclaimer: detail?.requiredDisclaimer || "",
    approvedForAutoSend: Boolean(detail?.approvedForAutoSend),
    requiresHumanApproval: Boolean(detail?.requiresHumanApproval),
    consentConfirmed: Boolean(detail?.consentConfirmed),
    isActive: detail?.isActive !== false,
    expiresAtUtc: formatDateInput(detail?.expiresAtUtc),
    language: detail?.language || "",
    tags: jsonArrayToList(detail?.tagsJson),
    useCases: jsonArrayToList(detail?.useCasesJson),
    priority: Number(detail?.priority) || 0,
    country: detail?.country || "",
    state: detail?.state || "",
    city: detail?.city || "",
    area: detail?.area || "",
    pincode: detail?.pincode || "",
  };
}

function validateMetadata(form, selectedPhoneNumberId, requireFile) {
  if (!selectedPhoneNumberId) return "Select a connected WhatsApp sender first.";
  if (!String(form.title || "").trim()) return "Title is required.";
  if (!form.category) return "Category is required.";
  if (requireFile) {
    const fileError = validateFile(form.file);
    if (fileError) return fileError;
  }
  if (
    form.approvedForAutoSend &&
    CONSENT_REQUIRED_CATEGORIES.has(form.category) &&
    !form.consentConfirmed
  ) {
    return "Consent confirmation is required before this category can be approved for auto-send.";
  }
  return null;
}

function buildMetadataFields(form) {
  return {
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim() || null,
    category: form.category,
    whatsAppMediaId: String(form.whatsAppMediaId || "").trim() || null,
    deliveryMessagePlacement: String(form.deliveryMessagePlacement || "").trim() || "CaptionOnly",
    beforeMediaMessageTemplate: String(form.beforeMediaMessageTemplate || "").trim() || null,
    afterMediaMessageTemplate: String(form.afterMediaMessageTemplate || "").trim() || null,
    approvedCaption: String(form.approvedCaption || "").trim() || null,
    requiredDisclaimer: String(form.requiredDisclaimer || "").trim() || null,
    approvedForAutoSend: Boolean(form.approvedForAutoSend),
    requiresHumanApproval: Boolean(form.requiresHumanApproval),
    consentConfirmed: Boolean(form.consentConfirmed),
    isActive: Boolean(form.isActive),
    expiresAtUtc: dateInputToUtc(form.expiresAtUtc),
    language: String(form.language || "").trim() || null,
    tagsJson: listToJsonArray(form.tags),
    useCasesJson: listToJsonArray(form.useCases),
    country: String(form.country || "").trim() || null,
    state: String(form.state || "").trim() || null,
    city: String(form.city || "").trim() || null,
    area: String(form.area || "").trim() || null,
    pincode: String(form.pincode || "").trim() || null,
    priority: Number(form.priority) || 0,
  };
}

function appendFormData(formData, key, value) {
  if (value === null || value === undefined || value === "") return;
  formData.append(key, String(value));
}

function MediaAssetForm({
  mode,
  form,
  setForm,
  selectedSender,
  onCancel,
  onSubmit,
  saving,
  error,
}) {
  const dialogRef = useAiAgentDialog(onCancel, saving);
  const consentRequired = CONSENT_REQUIRED_CATEGORIES.has(form.category);
  const showBeforeTemplate =
    form.deliveryMessagePlacement === "BeforeMedia" ||
    form.deliveryMessagePlacement === "BeforeAndAfterMedia";
  const showAfterTemplate =
    form.deliveryMessagePlacement === "AfterMedia" ||
    form.deliveryMessagePlacement === "BeforeAndAfterMedia";

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={mode === "edit" ? "Edit media asset" : "Upload media asset"}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {mode === "edit" ? "Edit media asset" : "Upload media asset"}
            </h2>
            <p className="text-xs text-slate-500">
              Sender: {selectedSender ? senderLabel(selectedSender) : "Not selected"}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close media asset form"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 px-5 py-5">
          {mode === "upload" ? (
            <label className="block text-sm font-medium text-slate-700">
              File
              <input
                type="file"
                accept={ACCEPTED_FILE_INPUT_TYPES}
                onChange={event => update("file", event.target.files?.[0] || null)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <span className="mt-1 block text-xs text-slate-500">
                JPG, PNG, WebP up to 10 MB. PDF up to 16 MB.
              </span>
              <span className="mt-1 block text-xs text-slate-500">
                WhatsApp-approved video formats: MP4 or 3GP. Keep file size within platform limit.
              </span>
            </label>
          ) : null}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Title
              <input
                value={form.title}
                onChange={event => update("title", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="Approved brochure"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Category
              <select
                value={form.category}
                onChange={event => update("category", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {CATEGORIES.map(category => (
                  <option key={category} value={category}>
                    {category.replace(/([a-z])([A-Z])/g, "$1 $2")}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Description
            <textarea
              value={form.description}
              onChange={event => update("description", event.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Approved caption
              <textarea
                value={form.approvedCaption}
                onChange={event => update("approvedCaption", event.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Required disclaimer
              <textarea
                value={form.requiredDisclaimer}
                onChange={event => update("requiredDisclaimer", event.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>
          </div>

          {mode === "edit" ? (
            <label className="block text-sm font-medium text-slate-700">
              WhatsApp Media ID
              <span className="ml-1.5 text-xs font-normal text-slate-400">
                set automatically on upload — only override if linking a pre-registered ID
              </span>
              <input
                value={form.whatsAppMediaId}
                onChange={event => update("whatsAppMediaId", event.target.value)}
                placeholder="e.g. 1234567890123456"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm"
              />
            </label>
          ) : null}

          <div className="rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Delivery messages</h3>
            <p className="mt-1 text-xs text-slate-500">
              Supports <code>{"{{CustomerName}}"}</code>.
            </p>

            <label className="mt-3 block text-sm font-medium text-slate-700">
              Delivery message placement
              <select
                value={form.deliveryMessagePlacement}
                onChange={event => update("deliveryMessagePlacement", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {DELIVERY_MESSAGE_PLACEMENTS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {showBeforeTemplate ? (
              <label className="mt-3 block text-sm font-medium text-slate-700">
                Before media message
                <textarea
                  value={form.beforeMediaMessageTemplate}
                  onChange={event => update("beforeMediaMessageTemplate", event.target.value)}
                  rows={3}
                  maxLength={1000}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <span className="mt-1 block text-xs text-slate-500">Supports {"{{CustomerName}}"}.</span>
              </label>
            ) : null}

            {showAfterTemplate ? (
              <label className="mt-3 block text-sm font-medium text-slate-700">
                After media message
                <textarea
                  value={form.afterMediaMessageTemplate}
                  onChange={event => update("afterMediaMessageTemplate", event.target.value)}
                  rows={3}
                  maxLength={1000}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <span className="mt-1 block text-xs text-slate-500">Supports {"{{CustomerName}}"}.</span>
              </label>
            ) : null}
          </div>

          <section aria-label="Media sending requirements" className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div><h3 className="text-sm font-medium text-slate-900">Sending requirements</h3><p className="mt-1 text-xs text-slate-500">Review the selected sender, approval, human review and consent together. Uploading or saving does not send a customer message.</p></div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.approvedForAutoSend}
                onChange={event => update("approvedForAutoSend", event.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">Approved for auto-send</span>
                <span className="text-xs text-slate-500">
                  Only approve media your business is allowed to send automatically.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.requiresHumanApproval}
                onChange={event => update("requiresHumanApproval", event.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">Requires human approval</span>
                <span className="text-xs text-slate-500">Keep review required by default.</span>
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={event => update("isActive", event.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">Active</span>
                <span className="text-xs text-slate-500">Inactive assets cannot be auto-sent.</span>
              </span>
            </label>
          </div>

          {consentRequired ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              This category can include customer proof or results. Confirm consent before approving it for auto-send.
              <label className="mt-2 flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.consentConfirmed}
                  onChange={event => update("consentConfirmed", event.target.checked)}
                />
                Consent confirmed
              </label>
            </div>
          ) : (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.consentConfirmed}
                onChange={event => update("consentConfirmed", event.target.checked)}
              />
              Consent confirmed
            </label>
          )}

          </section>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="block text-sm font-medium text-slate-700">
              Expires
              <input
                type="date"
                value={form.expiresAtUtc}
                onChange={event => update("expiresAtUtc", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Language
              <input
                value={form.language}
                onChange={event => update("language", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="en"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Priority
              <input
                type="number"
                value={form.priority}
                onChange={event => update("priority", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Tags
              <input
                value={form.tags}
                onChange={event => update("tags", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="pricing, guide"
              />
              <span className="mt-1 block text-xs text-slate-500">Comma-separated.</span>
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Use cases
              <input
                value={form.useCases}
                onChange={event => update("useCases", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="follow up, product info"
              />
              <span className="mt-1 block text-xs text-slate-500">Comma-separated.</span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            {["country", "state", "city", "area", "pincode"].map(field => (
              <label key={field} className="block text-sm font-medium capitalize text-slate-700">
                {field}
                <input
                  value={form[field]}
                  onChange={event => update(field, event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </label>
            ))}
          </div>

          {error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              disabled={saving}
            >
              {mode === "edit" ? <ShieldCheck className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Upload and save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AiMediaAssetsPage() {
  const { can, hasAllAccess } = useAuth() || {};
  const canManage =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_MEDIA_ASSETS_MANAGE));

  const [senders, setSenders] = React.useState([]);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(20);
  const [filters, setFilters] = React.useState({
    mediaType: "",
    category: "",
    isActive: "",
    approvedForAutoSend: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [sendersLoading, setSendersLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [modalMode, setModalMode] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editingDetail, setEditingDetail] = React.useState(null);
  const [formError, setFormError] = React.useState("");
  const [assetsError, setAssetsError] = React.useState("");
  const [sendersError, setSendersError] = React.useState("");
  const [detailError, setDetailError] = React.useState(null);
  const request = useAiRequestGuard({assets: [filters, page, pageSize, selectedPhoneNumberId], detail: [filters, selectedPhoneNumberId]});

  const senderById = React.useMemo(() => {
    const map = new Map();
    senders.forEach(sender => map.set(sender.phoneNumberId, sender));
    return map;
  }, [senders]);

  const selectedSender = selectedPhoneNumberId
    ? senderById.get(selectedPhoneNumberId)
    : null;

  const loadSenders = React.useCallback(async () => {
    const current = request("senders");
    setSendersLoading(true); setSendersError("");
    try {
      const rows = await getAiMediaAssetSenders();
      if (!current()) return;
      const normalized = normalizeSenderList(rows);
      setSenders(normalized);
      setSelectedPhoneNumberId(prev => {
        if (prev && normalized.some(sender => sender.phoneNumberId === prev)) {
          return prev;
        }
        const defaultSender =
          normalized.find(sender => sender.isDefault) || normalized[0] || null;
        return defaultSender?.phoneNumberId || "";
      });
    } catch (error) {
      if (!current()) return;
      setSendersError(getErrorMessage(error, "Failed to load WhatsApp senders.")); setSenders([]); setSelectedPhoneNumberId("");
    } finally {
      if (current()) setSendersLoading(false);
    }
  }, [request]);

  const loadAssets = React.useCallback(async () => {
    const current = request("assets");
    setLoading(true); setAssetsError(""); setItems([]); setTotal(0); setDetailError(null);
    try {
      const params = {
        page,
        pageSize,
      };
      if (selectedPhoneNumberId) params.phoneNumberId = selectedPhoneNumberId;
      if (filters.mediaType) params.mediaType = filters.mediaType;
      if (filters.category) params.category = filters.category;
      if (filters.isActive !== "") params.isActive = filters.isActive;
      if (filters.approvedForAutoSend !== "") {
        params.approvedForAutoSend = filters.approvedForAutoSend;
      }
      const result = await getAiMediaAssets(params);
      if (!current()) return;
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      if (current()) setAssetsError(getErrorMessage(error, "Failed to load AI media assets."));
    } finally {
      if (current()) setLoading(false);
    }
  }, [filters, page, pageSize, selectedPhoneNumberId, request]);

  React.useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  React.useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updateFilter = (field, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const openUpload = () => {
    setForm(EMPTY_FORM);
    setEditingDetail(null);
    setFormError("");
    setModalMode("upload");
  };

  const openEdit = async id => {
    if (!canManage) return;
    const current = request("detail"); setDetailError(null);
    try {
      const detail = await getAiMediaAsset(id);
      if (!current()) return;
      setEditingDetail(detail);
      setForm(toEditForm(detail));
      setFormError("");
      setModalMode("edit");
    } catch (error) {
      if (current()) setDetailError({id, message: getErrorMessage(error, "Failed to load media asset.")});
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setEditingDetail(null);
    setFormError("");
  };

  const submitUpload = async event => {
    event.preventDefault();
    const error = validateMetadata(form, selectedPhoneNumberId, true);
    if (error) {
      setFormError(error);
      return;
    }

    const metadata = buildMetadataFields(form);
    const formData = new FormData();
    formData.append("phoneNumberId", selectedPhoneNumberId);
    formData.append("file", form.file);
    Object.entries(metadata).forEach(([key, value]) => {
      appendFormData(formData, key, value);
    });

    setSaving(true);
    setFormError("");
    try {
      await uploadAiMediaAsset(formData);
      toast.success("Media asset uploaded and saved. Nothing was sent to any customer.");
      closeModal();
      await loadAssets();
    } catch (error) {
      setFormError(getErrorMessage(error, "Failed to upload media asset."));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async event => {
    event.preventDefault();
    if (!editingDetail?.id) return;
    const phoneNumberId = editingDetail.phoneNumberId || selectedPhoneNumberId;
    const error = validateMetadata(form, phoneNumberId, false);
    if (error) {
      setFormError(error);
      return;
    }

    const metadata = buildMetadataFields(form);
    const payload = {
      ...metadata,
    };

    setSaving(true);
    setFormError("");
    try {
      await updateAiMediaAsset(editingDetail.id, payload);
      toast.success("Media asset updated.");
      closeModal();
      await loadAssets();
    } catch (error) {
      setFormError(getErrorMessage(error, "Failed to update media asset."));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async id => {
    if (!canManage) return;
    const confirmed = window.confirm(
      "Deactivate this media asset? Approved for auto-send will be turned off."
    );
    if (!confirmed) return;

    try {
      await deleteAiMediaAsset(id);
      toast.success("Media asset deactivated. Approved for auto-send was turned off.");
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to deactivate media asset."));
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-slate-900">
                  Business knowledge
                </h1>
              </div>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Optional files your assistant can send through supported reply paths. A PDF asset does not add its contents to business knowledge.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  loadSenders();
                  loadAssets();
                }}
                aria-label="Refresh media library" title="Refresh media library"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                <RefreshCcw aria-hidden="true" className="h-4 w-4" />
              </button>
              {canManage ? (
                <button
                  type="button"
                  onClick={openUpload}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-emerald-600 px-3 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                  disabled={!selectedPhoneNumberId}
                >
                  <Upload className="h-4 w-4" />
                  Upload asset
                </button>
              ) : null}
            </div>
          </div>

          {!canManage ? (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              You can view media assets, but only admins can manage them.
            </p>
          ) : null}
        </div>

        <AiAnswerContentNav current="media" />
        <p className="flex items-start gap-2 px-5 pt-4 text-xs text-slate-500"><ShieldCheck aria-hidden="true" className="h-4 w-4 flex-none" />Approval is configuration, not a delivery guarantee. Active status, human review, consent, expiry and the exact WhatsApp sender still apply. Delayed replies support images only.</p>
        <AiLoadError message={sendersError} onRetry={loadSenders} loading={sendersLoading} label="senders" />
        <AiLoadError message={assetsError} onRetry={loadAssets} loading={loading} label="media" />
        <AiLoadError message={detailError?.message} onRetry={() => openEdit(detailError.id)} label="asset details" />
        <div className="border-b border-slate-200 bg-slate-50/60 p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
            <label className="block text-sm font-medium text-slate-700 lg:col-span-2">
              WhatsApp sender
              <select
                value={selectedPhoneNumberId}
                onChange={event => {
                  setPage(1);
                  setSelectedPhoneNumberId(event.target.value);
                }}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={sendersLoading}
              >
                <option value="">
                  {sendersLoading ? "Loading senders..." : "All senders"}
                </option>
                {senders.map(sender => (
                  <option key={sender.phoneNumberId} value={sender.phoneNumberId}>
                    {senderLabel(sender)}
                    {sender.isDefault ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Media type
              <select
                value={filters.mediaType}
                onChange={event => updateFilter("mediaType", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="">All types</option>
                <option value="Image">Image</option>
                <option value="Document">Document</option>
                <option value="Video">Video</option>
              </select>
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Category
              <select
                value={filters.category}
                onChange={event => updateFilter("category", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="">All categories</option>
                {CATEGORIES.map(category => (
                  <option key={category} value={category}>
                    {category.replace(/([a-z])([A-Z])/g, "$1 $2")}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-slate-700">
                Active
                <select
                  value={boolOptionValue(filters.isActive)}
                  onChange={event => updateFilter("isActive", fromBoolOption(event.target.value))}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Any</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Approved
                <select
                  value={boolOptionValue(filters.approvedForAutoSend)}
                  onChange={event =>
                    updateFilter("approvedForAutoSend", fromBoolOption(event.target.value))
                  }
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Any</option>
                  <option value="true">Approved</option>
                  <option value="false">Not approved</option>
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Approved media</h2>
              <p className="text-xs text-slate-500">{total} assets found</p>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Loading media assets...
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-3 text-sm font-medium text-slate-900">
                {assetsError ? "Media assets are unavailable. Retry the load." : "No media assets yet"}
              </p>
              <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500">
                Choose a WhatsApp sender and upload an image, PDF or supported video. Review sending requirements before approving automatic use.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Type / category</th>
                    <th className="px-4 py-3">WhatsApp sender</th>
                    <th className="px-4 py-3">Sending requirements</th>
                    <th className="px-4 py-3">Updated</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(item => {
                    const rowSender = senderById.get(item.phoneNumberId);
                    return (
                      <tr key={item.id} className="align-top">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{item.title}</div>
                          <div className="mt-1 max-w-[220px] truncate text-xs text-slate-500">
                            {item.fileName || "No filename"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700"><p>{item.mediaType}</p><p className="mt-1 text-xs text-slate-500">{String(item.category || "Other").replace(/([a-z])([A-Z])/g, "$1 $2")}</p></td>
                        <td className="px-4 py-3 text-slate-700">
                          {rowSender ? senderLabel(rowSender) : item.phoneNumberId}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`inline-flex rounded border px-2 py-0.5 ${badgeClasses(item.approvedForAutoSend)}`}>{item.approvedForAutoSend ? "Approved" : "Not approved"}</span>
                          <p className={`mt-1 ${item.requiresHumanApproval ? "text-amber-700" : "text-slate-500"}`}>{item.requiresHumanApproval ? "Human approval required" : "Human approval not required"}</p>
                          <p className={`mt-1 ${item.isActive ? "text-slate-600" : "text-amber-700"}`}>{item.isActive ? "Active" : "Inactive"}</p>
                          <p className={`mt-1 ${!item.consentConfirmed && CONSENT_REQUIRED_CATEGORIES.has(item.category) ? "text-amber-700" : "text-slate-500"}`}>{item.consentConfirmed ? "Consent confirmed" : "Consent not confirmed"}</p>
                          <p className="mt-1 text-slate-500">Expires: {item.expiresAtUtc ? formatDateTime(item.expiresAtUtc) : "Never"}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {formatDateTime(item.updatedAtUtc)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(item.id)}
                              disabled={!canManage}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              <Pencil className="h-3 w-3" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeactivate(item.id)}
                              disabled={!canManage}
                              className="rounded-lg border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            >
                              Deactivate
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalMode ? (
        <MediaAssetForm
          mode={modalMode}
          form={form}
          setForm={setForm}
          selectedSender={modalMode === "edit" ? senderById.get(editingDetail?.phoneNumberId) : selectedSender}
          onCancel={closeModal}
          onSubmit={modalMode === "edit" ? submitEdit : submitUpload}
          saving={saving}
          error={formError}
        />
      ) : null}
    </div>
  );
}

export default withAiAgentScope(AiMediaAssetsPage);
